import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import { watch } from 'vue';
import { syncToWebViewAction } from '@/actions/sync/SyncToWebViewAction.ts';
import { SYNC_TO_WEB_VIEW_KEYS } from '@/types/sync/SyncKeys.ts';
import type { SessionId } from '@/types/Session.ts';
import type { VariableConfig } from '@/types/edit/VariableConfig.ts';

interface State {
    // Streams
    streams: { [key: string]: string };
    streamsWaiting: string[];
    streamsInProgress: string[];
    streamsErrored: string[];
    streamErrors: Record<string, string>;
    streamsEnded: string[];
    streamStartDates: Record<string, number>;

    // Variables
    sessionVariables: Record<
        SessionId,
        Record<string, Record<VariableConfig['key'], VariableConfig['value']>>
    >;

    // Navigation
    verticalNavigationOptions: Record<string, Record<string, boolean>>;
    verticalNavigationSelection: Record<string, number>;

    // Run state
    activeSessionId: string | undefined;
    activeProfileId: string | undefined;
    screenCapturesBySession: Record<SessionId, string[]>;
    screenCaptureRevisions: Record<SessionId, number>;
}

export const useClientStore = defineStore('v3/client', {
    state: (): State => {
        return {
            // Streams
            streams: {},
            streamsWaiting: [],
            streamsInProgress: [],
            streamsErrored: [],
            streamErrors: {},
            streamsEnded: [],
            streamStartDates: {},

            // Variables
            sessionVariables: {},

            // Navigation
            verticalNavigationOptions: {},
            verticalNavigationSelection: {},

            // Run state
            activeSessionId: undefined,
            activeProfileId: undefined,
            screenCapturesBySession: {},
            screenCaptureRevisions: {},
        };
    },
    persist: {
        storage: sessionStorage,
        omit: ['screenCapturesBySession'],
    },
});

/**
 * @see storeSyncClientHandler
 * @see syncStoresToWebViewAction
 */
export const clientStoreWatcherHandler = (
    streamsWaiting: string[],
    streamStartDates: Record<string, number>,
    sessionVariables: Record<
        SessionId,
        Record<string, Record<VariableConfig['key'], VariableConfig['value']>>
    >,
    verticalNavigationOptions: Record<string, Record<number, boolean>>,
    verticalNavigationSelection: Record<string, number>,
    activeSessionId: string | undefined,
    activeProfileId: string | undefined,
    screenCaptureRevisions: Record<SessionId, number>,
) => {
    syncToWebViewAction(
        SYNC_TO_WEB_VIEW_KEYS.storeSyncClient,
        JSON.stringify({
            streamsWaiting: streamsWaiting,
            streamStartDates: streamStartDates,
            sessionVariables: sessionVariables,
            verticalNavigationOptions: verticalNavigationOptions,
            verticalNavigationSelection: verticalNavigationSelection,
            activeSessionId: activeSessionId,
            activeProfileId: activeProfileId,
            screenCaptureRevisions: screenCaptureRevisions,
        }),
    );
};

export const registerClientStoreWatcher = () => {
    const clientStore = useClientStore();
    const {
        streamsWaiting,
        streamStartDates,
        sessionVariables,
        verticalNavigationOptions,
        verticalNavigationSelection,
        activeSessionId,
        activeProfileId,
        screenCaptureRevisions,
    } = storeToRefs(clientStore);

    watch(
        [
            streamsWaiting,
            streamStartDates,
            sessionVariables,
            verticalNavigationOptions,
            verticalNavigationSelection,
            activeSessionId,
            activeProfileId,
            screenCaptureRevisions,
        ],
        async ([
            newStreamsWaiting,
            newStreamStartDates,
            newSessionVariables,
            newVerticalNavigationOptions,
            newVerticalNavigationSelection,
            newActiveSessionId,
            newActiveProfileId,
            newScreenCaptureRevisions,
        ]) => {
            clientStoreWatcherHandler(
                newStreamsWaiting,
                newStreamStartDates,
                newSessionVariables,
                newVerticalNavigationOptions,
                newVerticalNavigationSelection,
                newActiveSessionId,
                newActiveProfileId,
                newScreenCaptureRevisions,
            );
        },
        { deep: true },
    );
};

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useClientStore, import.meta.hot));
}
